import {
    Component,
    OnDestroy,
    OnInit,
    Input,
    ElementRef,
    ViewChild,
    Signal,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from "@angular/forms";

import { map, Subscription } from "rxjs";

import {
    SECONDARY_COLOR_LIGHT,
    TRANSPARENT_COLOR,
} from "@hermes/aphrodite/expansion-panel";

import { Product } from "@hermes/api-model-product";

import { FormHelper, hasSizeVariant } from "@hermes/fragments/product-utils";

import { PRODUCT_PAGE } from "@hermes/states/add-to-cart";
import { BasketFacade } from "@hermes/states/basket";
import {
    BUCKLE,
    LEATHER,
    SelectedVariantVariantsState,
    ProductPageFacade,
    SIZE,
} from "@hermes/states/product-page";
import { BreakpointService } from "@hermes/utils-generic/services/user-interface";

@Component({
    selector: "h-selector-template",
    templateUrl: "./selector-template.component.html",
    styleUrl: "./selector-template.component.scss",
})
export class SelectorTemplateComponent implements OnInit, OnDestroy {
    @ViewChild("ngForm", { static: true, read: ElementRef })
    public ngForm!: ElementRef;

    @ViewChild("sizeToggle", { read: ElementRef })
    public sizeToggle!: ElementRef;

    /* Form for AddToCart */
    public variantsForm: FormGroup = new FormGroup({});

    /** local buckle toggle state */
    public buckleToggleOpened: boolean = true;

    public subscription: Subscription = new Subscription();

    public isMobile!: Signal<boolean>;
    public selectedProductVariant!: SelectedVariantVariantsState;
    public transparentColor = TRANSPARENT_COLOR;
    public secondaryColorLight = SECONDARY_COLOR_LIGHT;
    public findInStorePressed: boolean = false;

    /** local leather toggle state */
    public leatherToggleOpened: boolean = true;

    /** local size toggle state */
    public sizeToggleOpened: boolean = false;

    /** In some functional cases we have to hide size alert */
    public hideSizeAlert: boolean = false;
    private inputProduct!: Product;

    constructor(
        public readonly productPageFacade: ProductPageFacade,
        private basketFacade: BasketFacade,
        private formHelper: FormHelper,
        private formBuilder: FormBuilder,
        private breakpointService: BreakpointService,
    ) {
        this.isMobile = toSignal(
            this.breakpointService
                .mediumBreakpointObserver()
                .pipe(map((value) => !value.matches)),
            { initialValue: false },
        );
    }

    public get product() {
        return this.inputProduct;
    }

    @Input()
    public set product(product: Product) {
        const isFirstChange = this.inputProduct === undefined;
        this.inputProduct = product;

        if (product) {
            this.initOrUpdateAddToCartForm(isFirstChange);
        }
    }

    public ngOnInit(): void {
        this.subscription.add(
            this.productPageFacade
                .selectedVariant(0)
                .subscribe((variants: SelectedVariantVariantsState) => {
                    this.selectedProductVariant = variants;
                }),
        );
    }

    public initOrUpdateAddToCartForm(isInit: boolean): void {
        this.hideSizeAlert = true;
        if (isInit) {
            this.variantsForm = this.formBuilder.group({});
            this.variantsForm.addControl(
                BUCKLE,
                new FormControl(this.product.beltkitAttributes?.skuBuckle),
            );
            this.variantsForm.addControl(
                LEATHER,
                new FormControl(
                    this.product.beltkitAttributes?.refColorisLeather,
                ),
            );

            this.variantsForm.addControl(
                SIZE,
                new FormControl("", Validators.required),
            );
        } else {
            this.variantsForm
                .get(BUCKLE)
                ?.setValue(this.product.beltkitAttributes?.skuBuckle);
            this.variantsForm
                .get(LEATHER)
                ?.setValue(this.product.beltkitAttributes?.refColorisLeather);
        }
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * Store new state of size toggle and send event of clicked toggle
     *
     * @param event boolean state of toggle collapse
     */
    public handleSizesToggled(event: { id: string; value: boolean }): void {
        this.sizeToggleOpened = !event.value;
        this.productPageFacade.showMoreVariant({
            id: SIZE,
            isExpanded: this.sizeToggleOpened,
        });
    }

    public shouldPreventProductLocatorLaunch() {
        return (
            Boolean(this.variantsForm.get(SIZE)?.invalid) &&
            hasSizeVariant(this.product)
        );
    }

    public findInStoreAction() {
        if (this.shouldPreventProductLocatorLaunch()) {
            this.showFormErrorMessage();
            this.findInStorePressed = true;
        }
    }

    /**
     * Return if form is valid or not - (variant selected or not)
     * Call to showFormErrorMessage() if invalid
     *
     * @returns isValid
     */
    public isFormValid(): boolean {
        return (
            this.variantsForm &&
            !this.variantsForm.invalid &&
            Boolean(this.selectedProductVariant.size?.sku)
        );
    }

    public showFormErrorMessage(): void {
        this.hideSizeAlert = false;
        this.sizeToggleOpened = true;
        this.scrollToError();
        if (this.ngForm) {
            this.formHelper.focusInvalidInput(this.ngForm);
        }
    }

    public addToCartAction(showNotifSuccess: boolean = true): void {
        if (!this.isFormValid()) {
            this.showFormErrorMessage();
            return;
        }
        if (this.selectedProductVariant.size?.sku) {
            this.basketFacade.addToCart({
                sku: this.selectedProductVariant.size?.sku,
                skuSet: this.product.beltkitAttributes?.skuSet,
                displayNotifOnSuccess: showNotifSuccess,
                itemPosition: 0,
                templateType: this.product.templateType,
                context: PRODUCT_PAGE,
            });
        }
    }

    public scrollToError(): void {
        if (this.isMobile()) {
            const element = this.sizeToggle.nativeElement as HTMLElement;
            element.scrollIntoView();
        }
    }
}
